import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { License, LicensedUserDTO, LicenseModule } from '@interfaces/LicenseController';
import { DateTime } from 'luxon';
import { NGXLogger } from 'ngx-logger';
import { EMPTY, firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BaseService } from './base.service';
import { UserService } from './user.service';

export class LicenseAppModule {
  public static Estimation = '7e90be2a-6373-477e-abb2-edc51a2a8e6a';
}

@Injectable({
  providedIn: 'root'
})
export class LicenseService extends BaseService {
  private license$: ReplaySubject<License> = null;

  private licensedUsers$: ReplaySubject<LicensedUserDTO[]> = null;

  private isTooManyUsersForModuleDict: { [module: string]: boolean } = {};

  public constructor(private http: HttpClient, private logger: NGXLogger, private userService: UserService) {
    super();
  }

  public uploadLicense(license: File): Observable<License> {
    this.license$ = new ReplaySubject<License>();
    return this.http.post(this.baseUrl + '/web/license/', license).pipe(
      tap((result: License) => {
        const license = result;
        this.license$.next(license);

        this.licensedUsers$ = null;
        this.getLicensedUsers().subscribe();
      })
    );
  }

  public getLicense(): Observable<License> {
    if (this.license$ !== null) {
      return this.license$;
    }
    this.license$ = new ReplaySubject<License>();

    return this.http.get<License>(this.baseUrl + '/web/license/').pipe(
      tap(result => {
        const license = result;
        this.license$.next(license);
      })
    );
  }

  public getLicensedUsers(): Observable<LicensedUserDTO[]> {
    if (this.licensedUsers$ !== null) {
      return this.licensedUsers$;
    }
    this.licensedUsers$ = new ReplaySubject<LicensedUserDTO[]>();

    return this.http.get(this.baseUrl + '/web/licensedUsers/GetUsers').pipe(
      tap((result: LicensedUserDTO[]) => {
        const licensedUsers = result;
        this.licensedUsers$.next(licensedUsers);
      }),
      catchError((error, caught) => {
        this.licenseErrorLogger(this.getLicensedUsers.name, error, caught);
        return EMPTY;
      })
    );
  }

  public addModulesForLicensedUsers(users: LicensedUserDTO[]): Observable<LicensedUserDTO[]> {
    return this.http.post(this.baseUrl + '/web/licensedUsers/AddLicensedUsers', users).pipe(
      tap((result: LicensedUserDTO[]) => {
        const licensedUsers = result;
        this.licensedUsers$.next(licensedUsers);
      })
    );
  }

  public removeModulesFromLicensedUsers(users: LicensedUserDTO[]): Observable<any> {
    return this.http.post(this.baseUrl + '/web/licensedUsers/RemoveLicensedUsers', users);
  }

  public isLicenseValid(moduleId: string): Observable<'no-license' | 'module-not-found' | 'valid' | 'expired'> {
    return this.getLicense().pipe(
      map(license => {
        if (!license) {
          return 'no-license';
        }

        const module: LicenseModule = license.modules.find(m => m.moduleId === moduleId);
        if (module == null) {
          return 'module-not-found';
        }
        if (DateTime.fromISO(module.expiration) < DateTime.now()) {
          return 'expired';
        }
        return 'valid';
      })
    );
  }

  public isUserAllowedToAccessModule(moduleId: string): Observable<boolean> {
    return this.userService.getUser().pipe(map(user => user.modules.filter(m => m === moduleId).length > 0));
  }

  public async isTooManyUsersForModule(moduleId: string, wantsToAddAnotherUser: boolean) {
    if (moduleId in this.isTooManyUsersForModuleDict) {
      // contains key
      return this.isTooManyUsersForModuleDict[moduleId];
    }

    const params = new HttpParams()
      .append('moduleId', moduleId)
      .append('wantsToAddAnotherUser', wantsToAddAnotherUser.toString());
    try {
      this.isTooManyUsersForModuleDict[moduleId] = await firstValueFrom(
        this.http.get<boolean>(this.baseUrl + '/web/licensedUsers/IsTooManyLicensedUsersForModule', { params: params })
      );
    } catch (error) {
      this.licenseErrorLogger(this.isTooManyUsersForModule.name, error);
    }
  }

  private licenseErrorLogger(functionName: string, error: Error, caught?: unknown): void {
    this.logger.error(`${functionName}: ${error}`);
    if (caught) {
      this.logger.error(`${functionName}: ${caught}`);
    }
  }
}
